import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Fila de producto en los resultados de la búsqueda predictiva
 * (snippets/emp-search-product-row.liquid).
 *
 * - Al elegir un tamaño actualiza la variante del formulario, el precio y el
 *   estado del botón agregar.
 * - Aísla el selector y el botón de los atajos del buscador:
 *   · el buscador devuelve el foco al input en cualquier clic que no sea
 *     botón/enlace/input (listener normal → basta con stopPropagation);
 *   · Horizon enruta `on:keydown` al ancestro más cercano con el atributo, que
 *     sería el formulario del buscador (se queda con Tab, flechas y Enter). Los
 *     controles van en un contenedor con su propio `on:keydown` apuntando aquí.
 * - Tras agregar al carrito cierra el buscador para que se vea el cajón.
 */
class EmpSearchRow extends Component {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abortController;

    this.select?.addEventListener('change', this.#onVariantChange, { signal });

    for (const control of this.querySelectorAll('select, button')) {
      control.addEventListener('click', stopPropagation, { signal });
    }

    this.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#abortController = new AbortController();
  }

  /** @returns {HTMLSelectElement | null} */
  get select() {
    return this.querySelector('[data-emp-variant-select]');
  }

  /**
   * Teclas dentro del selector o el botón: comportamiento nativo, salvo Escape,
   * que se sigue pasando al buscador como antes.
   * @param {KeyboardEvent} event
   */
  onControlKeyDown(event) {
    if (event.key !== 'Escape') return;

    const search = /** @type {(HTMLElement & {onSearchKeyDown?: (event: KeyboardEvent) => void}) | null} */ (
      this.closest('predictive-search-component')
    );
    search?.onSearchKeyDown?.(event);
  }

  #onVariantChange = () => {
    const option = this.select?.selectedOptions[0];
    if (!option) return;

    const variantInput = this.querySelector('input[name="id"]');
    if (variantInput instanceof HTMLInputElement) {
      variantInput.value = option.value;
      variantInput.disabled = option.disabled;
    }

    const price = this.querySelector('[data-emp-price]');
    if (price) price.textContent = option.dataset.price ?? '';

    const compare = this.querySelector('[data-emp-compare]');
    if (compare instanceof HTMLElement) {
      compare.textContent = option.dataset.comparePrice ?? '';
      compare.hidden = !option.dataset.comparePrice;
    }

    const button = this.querySelector('button[name="add"]');
    if (button instanceof HTMLButtonElement) button.disabled = option.disabled;
  };

  /** @param {Event & {detail?: {data?: {didError?: boolean}}}} event */
  #onCartUpdate = (event) => {
    if (event.detail?.data?.didError) return;

    const dialog = /** @type {(HTMLElement & {closeDialog?: () => void}) | null} */ (
      this.closest('dialog-component')
    );
    dialog?.closeDialog?.();
  };
}

/** @param {Event} event */
function stopPropagation(event) {
  event.stopPropagation();
}

if (!customElements.get('emp-search-row')) {
  customElements.define('emp-search-row', EmpSearchRow);
}
